/**
 * audienceSource.api.js — BYO-webhook campaign audience source CRUD + utilities.
 *
 * Backend endpoints:
 *   POST /campaigns/whatsapp/audience/presets/list
 *   POST /campaigns/whatsapp/audience/presets/create
 *   POST /campaigns/whatsapp/audience/presets/update  (also handles rotate_secret)
 *   POST /campaigns/whatsapp/audience/presets/delete
 *   POST /campaigns/whatsapp/audience/test            - fires a live probe to the preset webhook
 *   POST /campaigns/whatsapp/preview-contract         - generates JSON-Schema/cURL/snippets
 *
 * Response envelope: { success, data: {...} } or { success, data: [...] }
 * Signed-secret is returned ONCE in create/rotate — never in list/get.
 */

import axios from '../config/axiosConfig';

/* ────────────── presets ────────────── */

export const listPresets = () =>
  axios.post('/campaigns/whatsapp/audience/presets/list', {});

export const createPreset = (data) =>
  axios.post('/campaigns/whatsapp/audience/presets/create', data);

export const updatePreset = (preset_id, fields) =>
  axios.post('/campaigns/whatsapp/audience/presets/update', { preset_id, ...fields });

export const deletePreset = (preset_id) =>
  axios.post('/campaigns/whatsapp/audience/presets/delete', { preset_id });

// Rotate signing secret — uses the same update endpoint with rotate_secret: true
export const rotatePresetSecret = (preset_id) =>
  axios.post('/campaigns/whatsapp/audience/presets/update', { preset_id, rotate_secret: true });

/* ────────────── dry-run / contract ────────────── */

/**
 * Test the customer's webhook through our backend (NEVER from browser directly).
 * Returns:
 *   {
 *     ok: boolean,
 *     status_code: number,
 *     latency_ms: number,
 *     contacts_sample: [...],     // first 5 contacts
 *     columns_detected: [...],    // top-level keys found in each contact
 *     missing_required: ["phone"|"countrycode"|...],
 *     errors_per_row: [...],
 *     next_cursor_returned: string|null,
 *     total_count_returned: number|null,
 *   }
 */
export const testWebhook = ({ url, auth, preset_id, templateId, templatemapping, hard_send_cap }) =>
  axios.post('/campaigns/whatsapp/audience/test', {
    url,
    ...(preset_id ? { preset_id } : { auth }),
    template_id: templateId,
    templatemapping,
    hard_send_cap,
  });

/**
 * Generate the developer-facing contract (JSON Schema + cURL + Express + FastAPI examples)
 * for a given template + variable mapping. Used by <DeveloperContractModal />.
 */
export const previewContract = ({ templateId, templatemapping }) =>
  axios.post('/campaigns/whatsapp/preview-contract', {
    template_id: templateId,
    templatemapping,
  });

const audienceSourceApi = {
  listPresets,
  createPreset,
  updatePreset,
  deletePreset,
  rotatePresetSecret,
  testWebhook,
  previewContract,
};

export default audienceSourceApi;
